import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";
import { alpha, useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import type { IRetiradaPedidoSnapshot } from "interfaces/loja/pedidoRetirada";
import { labelStatusRetirada } from "utils/pedidoOperacional";
import { formatDate } from "helpers/formatDate";

type PedidoRetiradaResumoProps = {
  statusRetirada: string | null | undefined;
  ret: IRetiradaPedidoSnapshot | null | undefined;
  responsavelLabel: string;
  concluido: boolean;
  situacaoTitulo: string;
  codigoLabel: string;
  labelNome: string;
  labelDoc: string;
  labelTel: string;
  labelData: string;
};

function fmt(iso: string | null | undefined) {
  if (!iso) return "—";
  try {
    return formatDate(iso, "dd/MM/yyyy HH:mm:ss");
  } catch {
    return String(iso);
  }
}

function dash(v: string | null | undefined) {
  const t = v?.trim();
  return t ? t : "—";
}

export default function PedidoRetiradaResumo({
  statusRetirada,
  ret,
  responsavelLabel,
  concluido,
  situacaoTitulo,
  codigoLabel,
  labelNome,
  labelDoc,
  labelTel,
  labelData,
}: PedidoRetiradaResumoProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const borderSoft =
    mode === "dark" ? alpha("#c8e6c9", 0.45) : alpha("#7cb342", 0.22);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: concluido
          ? `2px solid ${theme.palette.success.main}`
          : `1px solid ${borderSoft}`,
        boxShadow: concluido
          ? `0 0 0 1px ${alpha(theme.palette.success.main, 0.2)}`
          : "0 2px 12px rgba(0,0,0,0.04)",
      }}
    >
      <MDBox p={2.5}>
        <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
          <Typography variant="button" fontWeight={700} color="primary">
            {situacaoTitulo}
          </Typography>
          <Chip
            size="small"
            label={labelStatusRetirada(statusRetirada ?? "PENDENTE")}
            color={
              concluido
                ? "success"
                : statusRetirada === "PRONTO_PARA_RETIRADA"
                  ? "warning"
                  : "default"
            }
          />
        </MDBox>
        <Grid container spacing={2}>
          {ret?.evento?.titulo ? (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Evento
              </Typography>
              <Typography variant="body2" fontWeight={600} mt={0.5}>
                {ret.evento.titulo}
              </Typography>
            </Grid>
          ) : null}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              {codigoLabel}
            </Typography>
            <Typography variant="body2" fontWeight={600} mt={0.5}>
              {dash(ret?.codigoRetirada)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              {labelNome}
            </Typography>
            <Typography variant="body2" fontWeight={600} mt={0.5}>
              {dash(ret?.nomeRecebedor)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              {labelDoc}
            </Typography>
            <Typography variant="body2" mt={0.5}>
              {dash(ret?.documentoRecebedor)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              {labelTel}
            </Typography>
            <Typography variant="body2" mt={0.5}>
              {dash(ret?.telefoneRecebedor)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              {labelData}
            </Typography>
            <Typography variant="body2" mt={0.5}>
              {fmt(ret?.retiradoEm)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              Responsável pela confirmação
            </Typography>
            <Typography variant="body2" mt={0.5}>
              {responsavelLabel}
            </Typography>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}
